import { getBackendFeedbackId } from './feedbackNumbering';
import { convertToBase64 } from './globalFunctions';

const FIREBASE_PREFIX = process.env.REACT_APP_FIREBASE_STORAGE_PREFIX;

/**
 * Build a Firebase storage URL from a frame path
 */
const buildFirebaseUrl = framePath => `${FIREBASE_PREFIX}${encodeURIComponent(framePath)}?alt=media`;

/**
 * Fetch an image and convert to base64 data URI, also returning dimensions
 */
const fetchImageAsBase64 = async url => {
  try {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit' });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    const blob = await response.blob();
    const base64 = await convertToBase64(blob);

    // Get image dimensions
    const dimensions = await new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = reject;
      img.src = base64;
    });

    return { base64, width: dimensions.width, height: dimensions.height };
  }
  catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to fetch image as base64:', url, error);

    return null;
  }
};

/**
 * Normalize score from review data
 */
const normalizeScore = rawScore => {
  if (rawScore == null) {
    return null;
  }

  let scoreDisplay = null;

  if (typeof rawScore === 'number') {
    scoreDisplay = String(rawScore);
  }
  else if (typeof rawScore === 'string' && rawScore.trim() !== '') {
    scoreDisplay = rawScore.trim().replace(/^\+/, '');
  }

  const scoreNumeric = scoreDisplay != null ? Number(scoreDisplay) : NaN;

  return isFinite(scoreNumeric) ? scoreDisplay : null;
};

/**
 * Build the complete PDF data object from raw review data
 */
export const buildPdfData = async reviewData => {
  const extraData = reviewData?.extra_data || {};
  const overview = extraData.post_processes?.overview?.data || {};
  const deeperInsightsData = extraData.post_processes?.deeper_insights?.feedback || [];

  // Build screen images
  const frameImages = extraData.frame_images || [];
  const frameNames = extraData.review_frame_names || [];

  // Fetch all images in parallel
  const imageResults = await Promise.all(
    frameImages.map(framePath => fetchImageAsBase64(buildFirebaseUrl(framePath)))
  );

  // Group final_feedback by page
  const feedbackByPage = {};

  (reviewData?.final_feedback || []).forEach(item => {
    if (item.dismiss) {
      return; // Skip dismissed findings
    }

    const page = item.page || 1;

    if (!feedbackByPage[page]) {
      feedbackByPage[page] = [];
    }
    feedbackByPage[page].push(item);
  });

  // Collect reason strings of deeper insights already present via final_feedback to avoid duplicates
  // (New Ideas reviews send the same items in both final_feedback and deeper_insights, but
  // final_feedback items lack uuid; reason field is identical across both sources)
  const deeperInsightReasonsFromFinalFeedback = new Set(
    (reviewData?.final_feedback || [])
      .filter(item => item.isDeeperInsight && item.reason)
      .map(item => item.reason)
  );

  // Merge deeper insights into feedbackByPage so they appear inline with other findings
  deeperInsightsData.forEach(item => {
    if (item.dismiss) {
      return;
    }

    if (item.reason && deeperInsightReasonsFromFinalFeedback.has(item.reason)) {
      return; // Already added via final_feedback, skip to avoid duplicates
    }

    const page = item.page || 1;

    if (!feedbackByPage[page]) {
      feedbackByPage[page] = [];
    }
    feedbackByPage[page].push({ ...item, isDeeperInsight: true });
  });

  // Build screens array
  let findingCounter = 0;
  // eslint-disable-next-line no-unused-vars
  const screens = frameImages.map((_, index) => {
    const pageNum = index + 1;
    const imageData = imageResults[index];
    const findings = (feedbackByPage[pageNum] || []).map(item => ({
      number: getBackendFeedbackId(item) ?? ++findingCounter,
      label: item.label || (item.isDeeperInsight ? 'New Ideas' : ''),
      category: item.isDeeperInsight ? 'deeper-insights' : (item.category || 'suggestion'),
      detailedFeedback: Array.isArray(item.detailed_feedback)
        ? item.detailed_feedback
        : (item.detailed_feedback ? [item.detailed_feedback] : []),
      reason: item.reason || '',
      psychologyPrinciple: item.psychology_principle || '',
      psychologyInsight: item.psychology_insight || '',
      pinLocation: item.pin_location || null,
      isDeeperInsight: item.isDeeperInsight || false,
      highImpactRecommendation: item.high_impact_recommendation || '',
      actionSteps: item.action_steps || [],
    }));

    return {
      name: frameNames[index] || `Screen ${pageNum}`,
      pageNum,
      totalPages: frameImages.length,
      imageBase64: imageData?.base64 || null,
      imageWidth: imageData?.width || 0,
      imageHeight: imageData?.height || 0,
      findings,
    };
  });

  return {
    title: extraData.file_name || extraData.review_frame_name || 'Design Review',
    score: normalizeScore(overview.score_assessment?.score),
    template: extraData.template || '',
    overview: {
      reviewSnippet: overview.review_snippet || '',
      areasOfImprovement: overview.areas_of_improvement || [],
      whatsWorking: overview.whats_working || [],
      primaryAudience: overview.primary_audience || '',
      primaryMetrics: overview.primary_metrics || [],
    },
    screens,
    generatedAt: new Date(),
  };
};
